/**
 * Mock data for the Accounting Recurring transactions module.
 *
 * The single source of truth for sample templates until the backend
 * ships. useRecurring.ts imports MOCK_RECURRING_TEMPLATES from here
 * and returns it as its `data`. When the API swap happens, only
 * useRecurring.ts changes; this file goes away.
 *
 * Also exports the label and description maps used across the module
 * (list page, builder, filter modal, print view, empty state).
 */

import type {
  RecurringTemplate,
  RecurringMode,
  RecurringTxnType,
  RecurringFrequency,
  RecurringSchedule,
  RecurringParty,
  RecurringTransactionSpec,
} from "./recurringTypes";

// ---------- Label and description maps ----------

export const MODE_LABELS: Record<RecurringMode, string> = {
  scheduled: "Scheduled",
  reminder: "Reminder",
  unscheduled: "Unscheduled",
};

export const MODE_DESCRIPTIONS: Record<RecurringMode, string> = {
  scheduled:
    "Fires automatically on the schedule. Posts the transaction to your ledger with no action needed.",
  reminder:
    "You get a reminder, you post it manually. Good for templates where the amount changes each run.",
  unscheduled:
    "Template only, no schedule. Run it on demand whenever you need a copy of the same transaction.",
};

export const TXN_TYPE_LABELS: Record<RecurringTxnType, string> = {
  expense: "Expense",
  bill: "Bill",
  invoice: "Invoice",
  journal: "Journal entry",
  transfer: "Transfer",
};

export const FREQUENCY_LABELS: Record<RecurringFrequency, string> = {
  daily: "Daily",
  weekly: "Weekly",
  biweekly: "Biweekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  semi_annually: "Semi-annually",
  annually: "Annually",
  custom: "Custom",
};

// ---------- Template factory ----------

const NOW_ISO = "2026-01-15T10:00:00.000Z";

interface TemplateArgs {
  id: string;
  name: string;
  mode: RecurringMode;
  txnType: RecurringTxnType;
  partyName: string;
  accountName: string;
  amount: number;
  frequency: RecurringFrequency;
  startDate: string;
  previousDate?: string | null;
  nextDate?: string | null;
  dayOfMonth?: number | "last";
  dayOfWeek?: number;
  interval?: number;
  status?: "active" | "paused" | "ended";
  runCount?: number;
  memo?: string;
}

/** Compact template factory. Keeps every row below one line of noise. */
function makeTemplate(a: TemplateArgs): RecurringTemplate {
  const party: RecurringParty = buildParty(a.txnType, a.id, a.partyName);
  const schedule: RecurringSchedule = {
    frequency: a.frequency,
    startDate: a.startDate,
    ...(a.dayOfMonth !== undefined ? { dayOfMonth: a.dayOfMonth } : {}),
    ...(a.dayOfWeek !== undefined ? { dayOfWeek: a.dayOfWeek } : {}),
    ...(a.interval !== undefined ? { interval: a.interval } : {}),
  };
  const transaction: RecurringTransactionSpec = {
    accountId: `acc-${a.accountName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
    accountName: a.accountName,
    amount: a.amount,
    currency: "CAD",
    ...(a.memo ? { memo: a.memo } : {}),
  };
  return {
    id: a.id,
    name: a.name,
    mode: a.mode,
    txnType: a.txnType,
    status: a.status ?? "active",
    party,
    transaction,
    schedule,
    previousDate: a.previousDate ?? null,
    nextDate: a.nextDate ?? null,
    runCount: a.runCount ?? 0,
    createdAt: NOW_ISO,
    updatedAt: NOW_ISO,
    createdBy: "me",
  };
}

function buildParty(
  txnType: RecurringTxnType,
  id: string,
  name: string,
): RecurringParty {
  switch (txnType) {
    case "invoice":
      return { type: "customer", customerId: `cust-${id}`, customerName: name };
    case "journal":
      return { type: "internal" };
    case "transfer":
      return { type: "transfer" };
    case "expense":
    case "bill":
      return { type: "supplier", supplierId: `sup-${id}`, supplierName: name };
  }
}

// ---------- The templates (28 total: 14 reminder + 11 scheduled + 3 unscheduled) ----------

export const MOCK_RECURRING_TEMPLATES: RecurringTemplate[] = [
  // ===== REMINDER MODE (14) =====
  makeTemplate({ id: "rem-payroll-biweekly", name: "Payroll biweekly", mode: "reminder", txnType: "journal", partyName: "", accountName: "Wages expense", amount: 3842.00, frequency: "biweekly", dayOfWeek: 5, startDate: "2026-01-02", previousDate: "2026-10-16", nextDate: "2026-10-30", runCount: 20, memo: "Biweekly payroll run" }),
  makeTemplate({ id: "rem-riverside-invoice", name: "Monthly client invoice", mode: "reminder", txnType: "invoice", partyName: "Riverside Clinic", accountName: "Sales income", amount: 1850.00, frequency: "monthly", dayOfMonth: 1, startDate: "2026-01-01", previousDate: "2026-09-30", nextDate: "2026-10-31", runCount: 9 }),
  makeTemplate({ id: "rem-enbridge-gas", name: "Enbridge natural gas", mode: "reminder", txnType: "expense", partyName: "Enbridge Gas Inc", accountName: "Utilities", amount: 142.75, frequency: "monthly", dayOfMonth: 10, startDate: "2026-01-10", previousDate: "2026-10-09", nextDate: "2026-11-09", runCount: 9 }),
  makeTemplate({ id: "rem-wcb-quarterly", name: "Workers comp (WCB)", mode: "reminder", txnType: "bill", partyName: "WCB Alberta", accountName: "Workers comp payable", amount: 480.00, frequency: "quarterly", dayOfMonth: "last", startDate: "2026-03-31", previousDate: "2026-09-29", nextDate: "2026-12-30", runCount: 3 }),
  makeTemplate({ id: "rem-gst-remittance", name: "GST remittance", mode: "reminder", txnType: "bill", partyName: "CRA GST/HST", accountName: "GST payable", amount: 2340.00, frequency: "quarterly", dayOfMonth: "last", startDate: "2026-03-31", previousDate: "2026-09-30", nextDate: "2026-12-31", runCount: 3 }),
  makeTemplate({ id: "rem-pst-remittance", name: "PST remittance", mode: "reminder", txnType: "bill", partyName: "Alberta Finance", accountName: "PST payable", amount: 890.00, frequency: "quarterly", dayOfMonth: "last", startDate: "2026-03-31", previousDate: "2026-09-30", nextDate: "2026-12-31", runCount: 3 }),
  makeTemplate({ id: "rem-epcor-electric", name: "EPCOR electricity", mode: "reminder", txnType: "expense", partyName: "EPCOR Utilities", accountName: "Utilities", amount: 318.40, frequency: "monthly", dayOfMonth: 12, startDate: "2026-01-12", previousDate: "2026-10-10", nextDate: "2026-11-12", runCount: 9 }),
  makeTemplate({ id: "rem-cleaning-weekly", name: "Office cleaning", mode: "reminder", txnType: "bill", partyName: "Spotless Cleaners Ltd", accountName: "Office expense", amount: 320.00, frequency: "weekly", dayOfWeek: 5, startDate: "2026-01-02", previousDate: "2026-10-17", nextDate: "2026-10-24", runCount: 42 }),
  makeTemplate({ id: "rem-rogers-phone", name: "Rogers business line", mode: "reminder", txnType: "expense", partyName: "Rogers Communications", accountName: "Internet and phone", amount: 189.50, frequency: "monthly", dayOfMonth: 22, startDate: "2026-01-22", previousDate: "2026-09-22", nextDate: "2026-11-22", runCount: 9 }),
  makeTemplate({ id: "rem-property-insurance", name: "Property insurance", mode: "reminder", txnType: "bill", partyName: "Intact Insurance", accountName: "Insurance", amount: 2840.00, frequency: "annually", dayOfMonth: 15, startDate: "2026-01-15", previousDate: "2026-01-15", nextDate: "2027-01-15", runCount: 1 }),
  makeTemplate({ id: "rem-business-license", name: "Business license renewal", mode: "reminder", txnType: "bill", partyName: "City of Edmonton", accountName: "Licenses and permits", amount: 310.00, frequency: "annually", dayOfMonth: 1, startDate: "2026-02-01", previousDate: "2026-02-01", nextDate: "2027-02-01", runCount: 1 }),
  makeTemplate({ id: "rem-chamber-dues", name: "Chamber of commerce dues", mode: "reminder", txnType: "expense", partyName: "Edmonton Chamber", accountName: "Dues and subscriptions", amount: 495.00, frequency: "annually", dayOfMonth: 1, startDate: "2026-03-01", previousDate: "2026-03-01", nextDate: "2027-03-01", runCount: 1 }),
  makeTemplate({ id: "rem-owner-distribution", name: "Owner distribution", mode: "reminder", txnType: "journal", partyName: "", accountName: "Owner equity", amount: 6000.00, frequency: "monthly", dayOfMonth: 15, startDate: "2026-01-15", previousDate: "2026-09-15", nextDate: "2026-11-15", runCount: 9 }),
  makeTemplate({ id: "rem-equipment-lease", name: "Equipment lease", mode: "reminder", txnType: "bill", partyName: "National Leasing", accountName: "Lease expense", amount: 1120.00, frequency: "monthly", dayOfMonth: 5, startDate: "2026-01-05", previousDate: "2026-10-05", nextDate: "2026-11-05", runCount: 9 }),

  // ===== SCHEDULED MODE (11) =====
  makeTemplate({ id: "sch-rent-king-st", name: "Rent · King St office", mode: "scheduled", txnType: "bill", partyName: "King Street Properties Ltd", accountName: "Rent expense", amount: 4200.00, frequency: "monthly", dayOfMonth: 1, startDate: "2026-01-01", previousDate: "2026-10-01", nextDate: "2026-11-01", runCount: 10, memo: "Monthly office rent" }),
  makeTemplate({ id: "sch-shopify-plus", name: "Shopify Plus subscription", mode: "scheduled", txnType: "expense", partyName: "Shopify Inc", accountName: "Software subscriptions", amount: 2000.00, frequency: "monthly", dayOfMonth: 14, startDate: "2026-01-14", previousDate: "2026-10-14", nextDate: "2026-11-14", runCount: 10 }),
  makeTemplate({ id: "sch-costco-card", name: "Costco Business card payment", mode: "scheduled", txnType: "expense", partyName: "Costco Wholesale", accountName: "Office supplies", amount: 1245.90, frequency: "monthly", dayOfMonth: "last", startDate: "2026-01-31", previousDate: "2026-09-30", nextDate: "2026-10-31", runCount: 9 }),
  makeTemplate({ id: "sch-atb-loan", name: "ATB equipment loan", mode: "scheduled", txnType: "bill", partyName: "ATB Financial", accountName: "Loan interest expense", amount: 1850.00, frequency: "monthly", dayOfMonth: 5, startDate: "2026-01-05", previousDate: "2026-10-05", nextDate: "2026-11-05", runCount: 10, memo: "Loan payment, interest + principal" }),
  makeTemplate({ id: "sch-vehicle-lease", name: "Vehicle lease · Ford F-150", mode: "scheduled", txnType: "bill", partyName: "Ford Credit Canada", accountName: "Vehicle lease expense", amount: 785.00, frequency: "monthly", dayOfMonth: 10, startDate: "2026-01-10", previousDate: "2026-10-10", nextDate: "2026-11-10", runCount: 10 }),
  makeTemplate({ id: "sch-google-workspace", name: "Google Workspace", mode: "scheduled", txnType: "expense", partyName: "Google Cloud", accountName: "Software subscriptions", amount: 180.00, frequency: "monthly", dayOfMonth: 7, startDate: "2026-01-07", previousDate: "2026-10-07", nextDate: "2026-11-07", runCount: 10 }),
  makeTemplate({ id: "sch-slack-business", name: "Slack Business+", mode: "scheduled", txnType: "expense", partyName: "Slack Technologies", accountName: "Software subscriptions", amount: 240.00, frequency: "monthly", dayOfMonth: 15, startDate: "2026-01-15", previousDate: "2026-10-15", nextDate: "2026-11-15", runCount: 10 }),
  makeTemplate({ id: "sch-zoom-pro", name: "Zoom Pro", mode: "scheduled", txnType: "expense", partyName: "Zoom Video", accountName: "Software subscriptions", amount: 95.00, frequency: "monthly", dayOfMonth: 20, startDate: "2026-01-20", previousDate: "2026-09-20", nextDate: "2026-10-20", runCount: 9 }),
  makeTemplate({ id: "sch-vehicle-insurance", name: "Vehicle insurance", mode: "scheduled", txnType: "bill", partyName: "SGI Canada", accountName: "Insurance", amount: 462.00, frequency: "monthly", dayOfMonth: 1, startDate: "2026-01-01", previousDate: "2026-10-01", nextDate: "2026-11-01", runCount: 10 }),
  makeTemplate({ id: "sch-domain-renewal", name: "Website domain renewal", mode: "scheduled", txnType: "expense", partyName: "GoDaddy Canada", accountName: "Software subscriptions", amount: 48.00, frequency: "annually", dayOfMonth: 1, startDate: "2026-11-01", previousDate: null, nextDate: "2026-11-01", runCount: 0 }),
  makeTemplate({ id: "sch-savings-transfer", name: "Monthly transfer to savings", mode: "scheduled", txnType: "transfer", partyName: "", accountName: "RBC Savings", amount: 5000.00, frequency: "monthly", dayOfMonth: 15, startDate: "2026-01-15", previousDate: "2026-10-15", nextDate: "2026-11-15", runCount: 10 }),

  // ===== UNSCHEDULED MODE (3) =====
  makeTemplate({ id: "uns-dividend", name: "Dividend payment template", mode: "unscheduled", txnType: "journal", partyName: "", accountName: "Dividends payable", amount: 15000.00, frequency: "custom", startDate: "2026-01-01", runCount: 2, memo: "Run after year-end once board approves amount" }),
  makeTemplate({ id: "uns-bonus-accrual", name: "Employee bonus accrual", mode: "unscheduled", txnType: "journal", partyName: "", accountName: "Bonus payable", amount: 8500.00, frequency: "custom", startDate: "2026-01-01", runCount: 1 }),
  makeTemplate({ id: "uns-adjust-writeoff", name: "Equipment write-off adjustment", mode: "unscheduled", txnType: "journal", partyName: "", accountName: "Accumulated depreciation", amount: 2340.00, frequency: "custom", startDate: "2026-01-01", runCount: 0 }),
];